import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Plus, Check, Bell, Clock, ChevronRight, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

interface FollowupReminder {
  id: string;
  leadId: string;
  dueDate: string;
  note: string | null;
  lead?: {
    id: string;
    leadName: string | null;
    contactName: string | null;
  } | null;
}

interface FollowupsData {
  dueToday: FollowupReminder[];
  dueTodayCount: number;
  dueThisWeekCount: number;
  all: FollowupReminder[];
}

interface FollowupsCardProps {
  className?: string;
}

export function FollowupsCard({ className }: FollowupsCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: followupsData, isLoading } = useQuery<FollowupsData>({
    queryKey: ["/api/followups"],
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/api/followups/${id}/complete`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/followups"] });
      toast({ title: "Reminder completed" });
    },
  });

  if (isLoading) {
    return (
      <Card className={cn("h-full flex flex-col", className)}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5" />
            Follow-up Reminders
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={cn("h-full flex flex-col", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5" />
              Follow-up Reminders
            </CardTitle>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsModalOpen(true)}
              data-testid="button-add-reminder"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col space-y-4">
          <div className="flex gap-4 mb-4">
            <div className="flex-1 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <p className="text-2xl font-bold text-orange-500">
                {followupsData?.dueTodayCount || 0}
              </p>
              <p className="text-xs text-muted-foreground">Due Today</p>
            </div>
            <div className="flex-1 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-2xl font-bold text-blue-500">
                {followupsData?.dueThisWeekCount || 0}
              </p>
              <p className="text-xs text-muted-foreground">Due This Week</p>
            </div>
          </div>

          {(!followupsData?.dueToday || followupsData.dueToday.length === 0) ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 text-muted-foreground">
              <Clock className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">No reminders due today</p>
              <p className="text-xs mt-1">Add a reminder to stay on track</p>
            </div>
          ) : (
            <div className="space-y-2">
              {followupsData.dueToday.slice(0, 5).map((reminder) => (
                <div
                  key={reminder.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover-elevate cursor-pointer group"
                  data-testid={`reminder-item-${reminder.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {reminder.lead?.leadName || reminder.lead?.contactName || "Unknown Lead"}
                    </p>
                    {reminder.note && (
                      <p className="text-xs text-muted-foreground truncate">
                        {reminder.note}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {format(new Date(reminder.dueDate), "h:mm a")}
                    </Badge>
                    {/*
                      The complete button is hover-only by design (keeps the
                      row tidy until the rep wants to act). For keyboard
                      users, force it visible on focus so the focus ring is
                      not invisible behind opacity-0.
                    */}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        completeMutation.mutate(reminder.id);
                      }}
                      data-testid={`button-complete-${reminder.id}`}
                      aria-label={`Mark reminder for ${reminder.lead?.leadName || reminder.lead?.contactName || "Unknown Lead"} as complete`}
                    >
                      <Check className="w-4 h-4 text-green-500" aria-hidden="true" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="pt-0 pb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-muted-foreground hover:text-foreground"
            data-testid="button-view-all-reminders"
            onClick={() => setIsViewAllOpen(true)}
          >
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardFooter>
      </Card>

      <AddFollowupModal open={isModalOpen} onOpenChange={setIsModalOpen} />
      <ViewAllRemindersModal 
        open={isViewAllOpen} 
        onOpenChange={setIsViewAllOpen}
        reminders={followupsData?.all || []}
        onComplete={(id) => completeMutation.mutate(id)}
      />
    </>
  );
}

function AddFollowupModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [leadSearch, setLeadSearch] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [dueOption, setDueOption] = useState("today");
  const [customDate, setCustomDate] = useState("");
  const [note, setNote] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: leads, isLoading: leadsLoading } = useQuery<Array<{ id: string; leadName: string | null; contactName: string | null }>>({
    queryKey: ["/api/leads/search", leadSearch],
    queryFn: async () => {
      const res = await fetch(`/api/leads/search?q=${encodeURIComponent(leadSearch)}`);
      if (!res.ok) throw new Error("Failed to search leads");
      return res.json();
    },
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: async (data: { leadId: string; dueDate: string; note: string | null }) => {
      await apiRequest("POST", "/api/followups", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/followups"] });
      toast({ title: "Reminder created" });
      onOpenChange(false);
      resetForm();
    },
    onError: () => {
      toast({ title: "Failed to create reminder", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setLeadSearch("");
    setSelectedLeadId("");
    setDueOption("today");
    setCustomDate("");
    setNote("");
  };

  const handleSubmit = () => {
    if (!selectedLeadId) {
      toast({ title: "Please select a lead", variant: "destructive" });
      return;
    }

    let dueDate: Date;
    const today = new Date();
    today.setHours(9, 0, 0, 0);

    if (dueOption === "today") {
      dueDate = today;
    } else if (dueOption === "tomorrow") {
      dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + 1);
    } else {
      if (!customDate) {
        toast({ title: "Please select a date", variant: "destructive" });
        return;
      }
      dueDate = new Date(customDate);
      dueDate.setHours(9, 0, 0, 0);
    }

    createMutation.mutate({
      leadId: selectedLeadId,
      dueDate: dueDate.toISOString(),
      note: note || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Follow-up Reminder</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Lead</Label>
            <Input
              placeholder="Search leads..."
              value={leadSearch}
              onChange={(e) => setLeadSearch(e.target.value)}
              data-testid="input-lead-search"
            />
            {leads && leads.length > 0 && (
              <div className="max-h-40 overflow-y-auto border rounded-md">
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    className={cn(
                      "px-3 py-2 cursor-pointer hover:bg-muted",
                      selectedLeadId === lead.id && "bg-primary/10"
                    )}
                    onClick={() => setSelectedLeadId(lead.id)}
                    data-testid={`lead-option-${lead.id}`}
                  >
                    {lead.leadName || lead.contactName || "Unnamed Lead"}
                  </div>
                ))}
              </div>
            )}
            {selectedLeadId && (
              <p className="text-sm text-muted-foreground">
                Selected: {leads?.find((l) => l.id === selectedLeadId)?.leadName || "Lead"}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Select value={dueOption} onValueChange={setDueOption}>
              <SelectTrigger data-testid="select-due-option">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="tomorrow">Tomorrow</SelectItem>
                <SelectItem value="custom">Custom Date</SelectItem>
              </SelectContent>
            </Select>
            {dueOption === "custom" && (
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                data-testid="input-custom-date"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea
              placeholder="Add a note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="textarea-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedLeadId || createMutation.isPending}
            data-testid="button-create-reminder"
          >
            {createMutation.isPending ? "Creating..." : "Create Reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewAllRemindersModal({
  open,
  onOpenChange,
  reminders,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reminders: FollowupReminder[];
  onComplete: (id: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            All Follow-up Reminders
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {reminders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No reminders</p>
            </div>
          ) : (
            reminders.map((reminder) => (
              <div
                key={reminder.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {reminder.lead?.leadName || reminder.lead?.contactName || "Unknown Lead"}
                  </p>
                  {reminder.note && (
                    <p className="text-sm text-muted-foreground truncate">{reminder.note}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Due: {format(new Date(reminder.dueDate), "MMM d, yyyy")}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onComplete(reminder.id)}
                  className="ml-2 flex-shrink-0"
                  data-testid={`button-complete-reminder-${reminder.id}`}
                  aria-label={`Mark reminder for ${reminder.lead?.leadName || reminder.lead?.contactName || "Unknown Lead"} as complete`}
                >
                  <Check className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
